import { useMemo } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, Inbox, KanbanSquare, LayoutDashboard, Menu, Settings, Search } from 'lucide-react';
import { get, qs } from '../lib/api';
import { useMe } from '../lib/session';
import { useClientSettings } from '../lib/settings';
import { UploadIndicator } from '../uploads/UploadIndicator';
import { NotificationBell } from './NotificationBell';
import { ThemeIconButton } from './ThemeToggle';
import { BottomTabs } from './ClientShell';
import { Avatar, setBusinessOrder } from '../ui/misc';
import { cn } from '../lib/cn';
import { Logo } from './Logo';

type Biz = { id: string; name: string; slug: string };

export const STAFF_BIZ_KEY = ['businesses'] as const;

/** Staff frame: dark sidebar at ≥ 1024 px (S4/S5), bottom tabs on phones (§11b, §13). */
export function StaffShell() {
  const me = useMe();
  useClientSettings();
  const biz = useQuery({
    queryKey: STAFF_BIZ_KEY,
    queryFn: async () => {
      const list = await get<Biz[]>('/api/businesses');
      setBusinessOrder(list.map((b) => b.id));
      return list;
    },
    staleTime: 5 * 60_000,
  });
  const counts = useQuery({
    queryKey: ['tasks', 'counts', 'all'],
    queryFn: () => get<Record<string, number>>(`/api/tasks/counts${qs({})}`),
    staleTime: 30_000,
  });
  const newCount = counts.data?.submitted ?? 0;
  const names = useMemo(() => Object.fromEntries((biz.data ?? []).map((b) => [b.id, b.name])), [biz.data]);

  const items = [
    { to: '/staff', label: 'Overview', Icon: LayoutDashboard, end: true },
    { to: '/staff/inbox', label: 'Requests', Icon: Inbox, badge: newCount },
    { to: '/staff/board', label: 'All tasks', Icon: KanbanSquare },
    { to: '/staff/schedule', label: 'Schedule & workload', Icon: CalendarDays },
    { to: '/staff/businesses', label: 'Clients & businesses', Icon: Building2 },
    ...(me.role === 'admin' ? [{ to: '/admin', label: 'Admin console', Icon: Settings }] : []),
  ];

  return (
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded-lg bg-card px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-dvh w-[260px] shrink-0 flex-col bg-sidebar px-4 py-5 lg:flex">
        <Logo inverted to="/staff" suffix="Team" className="px-2" />
        <nav aria-label="Team" className="mt-8 flex flex-col gap-1">
          {items.map(({ to, label, Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex h-11 items-center gap-3 rounded-lg px-3 text-[15px]',
                  isActive ? 'bg-sidebar-active font-semibold text-sidebar-ink-strong' : 'text-sidebar-ink hover:bg-sidebar-active/60',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon aria-hidden className="size-[18px]" />
                  <span className="flex-1">{label}</span>
                  {badge ? (
                    <span className={cn('min-w-6 rounded-full px-1.5 text-center text-xs leading-6 font-semibold text-white', isActive ? 'bg-primary' : 'bg-sidebar-badge')}>
                      {badge}
                      <span className="sr-only"> new</span>
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/me" className="mt-auto flex items-center gap-3 rounded-lg border-t border-sidebar-active px-2 pt-4 hover:opacity-90">
          <Avatar name={me.name} size={36} className="bg-sidebar-badge! text-white!" />
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-medium text-sidebar-ink-strong">{me.name}</span>
            <span className="block text-[13px] text-sidebar-muted">{me.role === 'admin' ? 'Admin' : 'Team'}</span>
          </span>
        </NavLink>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-card pt-[env(safe-area-inset-top)] lg:border-b-0 lg:bg-transparent">
          <div className="flex h-14 items-center gap-1 px-3 sm:px-6 lg:h-16 lg:px-8">
            <Logo to="/staff" className="lg:hidden" />
            <NavLink
              to="/staff/inbox?focus=search"
              className="ml-auto inline-flex h-11 items-center gap-2 rounded-lg px-3 text-[15px] text-muted hover:bg-surface lg:hidden"
            >
              <Search aria-hidden className="size-4" />
              <span className="sr-only">Search requests</span>
            </NavLink>
            <div className="flex items-center gap-1 lg:ml-auto">
              <UploadIndicator businessNames={names} />
              <NotificationBell />
              <ThemeIconButton />
            </div>
          </div>
        </header>
        <main id="main" tabIndex={-1} className="w-full flex-1 px-3 pt-3 pb-[calc(5.5rem+env(safe-area-inset-bottom))] outline-none sm:px-6 lg:px-8 lg:pt-2 lg:pb-12">
          <Outlet />
        </main>
      </div>

      <BottomTabs
        label="Team"
        tabs={[
          { to: '/staff', label: 'Overview', Icon: LayoutDashboard, end: true },
          { to: '/staff/inbox', label: 'Inbox', Icon: Inbox, badge: newCount },
          { to: '/staff/board', label: 'Board', Icon: KanbanSquare },
          { to: '/staff/schedule', label: 'Schedule', Icon: CalendarDays },
          { to: '/staff/more', label: 'More', Icon: Menu },
        ]}
      />
    </div>
  );
}
